import { useCallback, useRef } from 'react';
import orderService from '../services/order';
import { playAdminChime } from '../helpers/adminChime';
import useBackgroundPoll from '../helpers/useBackgroundPoll';

/**
 * Watches for orders arriving, from anywhere in the dashboard.
 *
 * Before this, a new order announced itself to nobody. The list fetched once
 * when you opened it and then sat there — `autoRefreshOrder` existed as a
 * helper but was never called from anywhere — so an admin with the orders page
 * open watched a screen that would not change until they pressed reload. On a
 * shop where the whole point is to notice an order quickly, that is the wrong
 * default.
 *
 * Two things happen when one appears:
 *
 *  - **A sound**, because the tab is usually not the one being looked at.
 *  - **An event**, which the order list picks up and reloads on — with the
 *    filters and page the admin already had, not a reset to the top.
 *
 * Polled rather than pushed. The backend has no socket for this, and a request
 * for one page of orders every twenty seconds is cheap next to the alternative
 * of an admin refreshing by hand.
 */

export const ADMIN_NEW_ORDER_EVENT = 'ks:admin-new-order';

/** Matches NotificationBar and the chat watcher so the badges move together. */
const POLL_MS = 20000;

/** In a background tab: still listening for a new order, four times cheaper. */
const BACKGROUND_POLL_MS = 80000;
/** Enough to notice a burst between polls without asking for a whole page. */
const PAGE_SIZE = 10;

const idOf = (row) => String(row?.id ?? row?._id ?? row?.uuid ?? '').trim();

export default function AdminOrderWatch() {
  /**
   * Order ids from the previous poll.
   *
   * `null` until the first response, which is what stops every order already in
   * the shop announcing itself the moment the dashboard opens. Only ids that
   * appear after that first look count as new.
   */
  const knownIds = useRef(null);

  const poll = useCallback(async () => {
    try {
      const res = await orderService.getAll({ page: 1, perPage: PAGE_SIZE });
      const list = Array.isArray(res?.data) ? res.data : (res?.data?.data ?? []);
      if (!Array.isArray(list) || !list.length) return;

      const ids = new Set(list.map(idOf).filter(Boolean));
      if (!ids.size) return;

      if (knownIds.current) {
        const appeared = [...ids].filter((id) => !knownIds.current.has(id));
        if (appeared.length) {
          playAdminChime('order');
          window.dispatchEvent(
            new CustomEvent(ADMIN_NEW_ORDER_EVENT, {
              detail: { count: appeared.length, ids: appeared },
            }),
          );
        }
      }
      knownIds.current = ids;
    } catch {
      // A failed poll changes nothing: the admin keeps the list they had, and
      // the next tick tries again. Never surface this — it is background work
      // nobody asked for, and a toast for it would be noise.
    }
  }, []);

  useBackgroundPoll(poll, { visibleMs: POLL_MS, hiddenMs: BACKGROUND_POLL_MS });

  return null;
}
